import * as PopoverPrimitive from '@radix-ui/react-popover';
import { forwardRef } from 'react';
import Icon from '../Icon';
import { DEFAULT_TOOL_TIP_DELAY } from '../Tooltip/Tooltip.types';
import { getSvgIconPath } from '../utils/svg';
import type { QTipDataAttributes } from '../Tooltip/QTip.types';
import type { ToolbarButtonProps } from './ToolbarButton.types';

const popoverBorderStyles = [
  'tw:border-solid',
  'tw:border',
  'tw:rounded-md',
  'tw:border-sq-disabled-gray',
  'tw:dark:border-gray-500',
].join(' ');

const triggerBackgroundStyles = [
  'tw:bg-transparent',
  'tw:hover:bg-sq-worksheetspanel-gray',
  'tw:active:bg-sq-worksheetspanel-gray',
  'tw:dark:bg-transparent',
  'tw:dark:hover:bg-sq-field-disabled-gray/30',
  'tw:dark:active:bg-sq-field-disabled-gray/30',
].join(' ');

const activeBackgroundStyles = [
  'active',
  'tw:bg-sq-overlay',
  'tw:active:bg-sq-overlay',
  'tw:dark:bg-sq-dark-disabled-gray',
  'tw:dark:hover:border-sq-dark-disabled-gray/30',
  'tw:dark:active:bg-sq-dark-disabled-gray/30',
].join(' ');

const bgStyles = ['tw:bg-sq-white', 'tw:dark:bg-sq-dark-background'].join(' ');

const disabledClasses = ['tw:opacity-50', 'tw:cursor-not-allowed'].join(' ');

const PopoverContent = forwardRef<
  React.ComponentRef<typeof PopoverPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof PopoverPrimitive.Content>
>(({ align = 'center', sideOffset = 4, ...props }, ref) => (
  <PopoverPrimitive.Content ref={ref} align={align} sideOffset={sideOffset} {...props} asChild />
));
PopoverContent.displayName = 'PopoverContent';

export const ToolbarButton = ({
  isSmall = false,
  label,
  icon,
  secondIcon,
  forceSmallIcon = false,
  tooltipText,
  id,
  popoverContent,
  extraClassNames,
  testId,
  tooltipTestId,
  tooltipOptions,
  isActive = false,
  isHtmlTooltip = false,
  hasArrow = false,
  disabled = false,
  isPrimaryAnSvg = false,
  primaryIconExtraClassNames,
  iconHeight = 24,
  iconWidth = 24,
  primaryIconViewbox = '0 0 24 24',
  popoverAlign = 'start',
  onClick,
  onHide,
}: ToolbarButtonProps) => {
  let tooltipData: QTipDataAttributes | undefined;

  if (tooltipText) {
    tooltipData = {
      'data-qtip-text': tooltipText,
      'data-qtip-placement': tooltipOptions?.position,
      'data-qtip-is-html': isHtmlTooltip,
      'data-qtip-testid': tooltipTestId,
      'data-qtip-delay': tooltipOptions?.delay ?? DEFAULT_TOOL_TIP_DELAY,
    };
  }

  const onOpenChange = (open: boolean) => {
    if (!open) {
      onHide && onHide();
    }
  };

  return (
    <PopoverPrimitive.Root defaultOpen={false} onOpenChange={onOpenChange}>
      <PopoverPrimitive.Trigger
        id={id}
        className={`tw:bg-transparent tw:border-none tw:mx-sq-1.5 tw:first:ml-0 tw:last:mr-0 ${
          isActive ? 'active' : ''
        }`}
        disabled={disabled}
        data-testid={testId}
        onClick={(e) => {
          onClick && onClick(e);
        }}>
        <div
          {...tooltipData}
          className={`tw:flex tw:flex-col tw:items-center tw:rounded-md ${
            isSmall ? 'tw:py-px tw:px-1' : 'tw:px-2 tw:py-sq-5'
          } ${disabled ? disabledClasses : isActive ? activeBackgroundStyles : triggerBackgroundStyles} ${
            extraClassNames || ''
          }`}>
          <span className="tw:nowrap">
            {isPrimaryAnSvg ? (
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className={`tw:text-sq-text-color tw:dark:text-sq-dark-text ${primaryIconExtraClassNames || ''}`}
                viewBox={primaryIconViewbox}
                height={`${iconHeight}px`}
                width={`${iconWidth}px`}
                fill="currentColor"
                data-testid={testId}>
                <path d={getSvgIconPath(icon)} />
              </svg>
            ) : (
              <Icon
                icon={icon}
                testId="firstIcon"
                type="text"
                size={!isSmall && !label && !secondIcon && !forceSmallIcon ? 'lg' : 'sm'}
                extraClassNames="tw:text-sq-text-color tw:dark:text-sq-dark-text"
              />
            )}

            {secondIcon && <Icon icon={secondIcon} type="text" testId="secondIcon" />}
            {popoverContent ? (
              <Icon
                icon="fc-arrow-dropdown"
                extraClassNames="tw:text-sq-text-color tw:dark:text-sq-dark-text tw:ml-sq-3 tw:text-2"
                type="text"
                size="sm"
              />
            ) : undefined}
          </span>
          {!isSmall && <small className="tw:text-sq-text-color tw:dark:text-sq-dark-text tw:text-2.5">{label}</small>}
        </div>
      </PopoverPrimitive.Trigger>
      {popoverContent ? (
        <PopoverContent sideOffset={2} align={popoverAlign}>
          <div
            className={
              `${
                bgStyles
              } tw:relative tw:z-[1000] tw:min-w-6 tw:p-2 tw:focus-visible:outline-none tw:outline-none tw:data-[state=open]:animate-in tw:data-[state=closed]:animate-out` +
              ` tw:data-[state=closed]:fade-out-0 tw:data-[state=open]:fade-in-0 tw:data-[state=closed]:zoom-out-95 tw:data-[state=open]:zoom-in-95 tw:data-[side=bottom]:slide-in-from-top-2 tw:data-[side=left]:slide-in-from-right-2 tw:data-[side=right]:slide-in-from-left-2 tw:data-[side=top]:slide-in-from-bottom-2${
                popoverBorderStyles
              }`
            }>
            {hasArrow && (
              <PopoverPrimitive.Arrow asChild>
                <div className=" tw:fill-transparent tw:bg-sq-white tw:w-sq-15 tw:h-sq-15 tw:-mt-sq-7 tw:rotate-45 tw:dark:bg-sq-dark-background tw:border-b tw:border-r tw:border-sq-disabled-gray tw:dark:border-gray-500" />
              </PopoverPrimitive.Arrow>
            )}
            {popoverContent}
          </div>
        </PopoverContent>
      ) : undefined}
    </PopoverPrimitive.Root>
  );
};
